import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import membresiaVideo from "../../static/media/Home/membresia.mp4";

const MotionBox = motion(Box);
const MotionButton = motion(Button);

const COLORS = {
  bg: "#ffffff",
  text: "#111111",
  muted: "rgba(17,17,17,0.72)",
  line: "rgba(17,17,17,0.08)",
  red: "#e50914",
};

export default function MembershipVideoSection() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useMemo(
    () => [
      "Membresía de ingreso a nuestras instalaciones.",
      "Controles semanales de composición corporal.",
      "Entrenamiento uno a uno con el coach.",
      "Reserva a elección del horario de entrenamiento bajo revisión de disponibilidad.",
    ],
    []
  );

  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <>
      <Box
        component="section"
        sx={{
          py: { xs: 3, md: 7 },
          bgcolor: COLORS.bg,
          color: COLORS.text,
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            width: { xs: "90%", md: "80%" },
            px: 0,
          }}
        >
          <Box
            sx={{
              borderRadius: { xs: 4, md: 5 },
              overflow: "hidden",
              border: `1px solid ${COLORS.line}`,
              boxShadow: "0 20px 60px rgba(0,0,0,0.06)",
              bgcolor: "#ffffff",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "1.1fr 0.9fr" },
                alignItems: "stretch",
              }}
            >
              <Box
                sx={{
                  p: { xs: 2, md: 3 },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: 13, md: 14 },
                    fontWeight: 800,
                    letterSpacing: 1.1,
                    textTransform: "uppercase",
                    color: COLORS.red,
                  }}
                >
                  Entrena mejor con tu plan
                </Typography>

                <Typography
                  sx={{
                    mt: 0.8,
                    fontSize: { xs: 24, md: 34 },
                    lineHeight: 0.98,
                    letterSpacing: -1,
                    fontWeight: 800,
                    color: "#111111",
                    maxWidth: 620,
                  }}
                >
                  Aprende cómo sacarle más provecho a tu membresía
                </Typography>

                <Typography
                  sx={{
                    mt: 1.1,
                    fontSize: { xs: 14.5, md: 16 },
                    lineHeight: 1.7,
                    color: COLORS.muted,
                    fontWeight: 600,
                    maxWidth: 750,
                  }}
                >
                  Conoce la mejor forma de entrenar dentro de NovaXFit según el tipo de membresía que elijas.
                </Typography>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.2}
                  sx={{ mt: 2 }}
                >
                  <MotionButton
                    variant="contained"
                    startIcon={<PlayArrowRoundedIcon />}
                    onClick={() => setVideoOpen(true)}
                    initial={{ opacity: 0, y: 18, scale: 0.96 }}
                    whileInView={{ opacity: 1, y: 0, scale: 1 }}
                    whileHover={{ y: -2, scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.45, ease: "easeOut" }}
                    sx={{
                      alignSelf: "flex-start",
                      px: 2.8,
                      py: 1.2,
                      borderRadius: 999,
                      textTransform: "none",
                      fontSize: 15.5,
                      fontWeight: 700,
                      color: "#ffffff",
                      background:
                        "linear-gradient(135deg, #1a1a1a 0%, #3a0a0f 25%, #e50914 55%, #8c0d15 75%, #121212 100%)",
                      backgroundSize: "200% 200%",
                      boxShadow:
                        "0 16px 36px rgba(229,9,20,0.28), inset 0 1px 0 rgba(255,255,255,0.22), inset 0 -1px 0 rgba(0,0,0,0.28)",
                      border: "1px solid rgba(0,0,0,0.15)",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #262626 0%, #520b12 25%, #ff1f2c 55%, #a3121b 75%, #1a1a1a 100%)",
                        boxShadow:
                          "0 20px 44px rgba(229,9,20,0.35), inset 0 1px 0 rgba(255,255,255,0.28), inset 0 -1px 0 rgba(0,0,0,0.32)",
                      },
                    }}
                  >
                    Ver video
                  </MotionButton>
                </Stack>
              </Box>

              <Box
                sx={{
                  p: { xs: 1.5, md: 2 },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: { xs: "center", lg: "flex-end" },
                  bgcolor: { xs: "#ffffff", lg: "rgba(17,17,17,0.02)" },
                }}
              >
                <MotionBox
                  onClick={() => setVideoOpen(true)}
                  initial={{ opacity: 0, x: 26, scale: 0.96 }}
                  whileInView={{ opacity: 1, x: 0, scale: 1 }}
                  whileHover={{ y: -6, scale: 1.02 }}
                  viewport={{ once: true, amount: 0.35 }}
                  transition={{ duration: 0.55, ease: "easeOut" }}
                  sx={{
                    width: { xs: "100%", sm: 280, md: 310 },
                    maxWidth: 310,
                    borderRadius: 4,
                    overflow: "hidden",
                    position: "relative",
                    cursor: "pointer",
                    bgcolor: "#0f0f10",
                    boxShadow: "0 22px 50px rgba(0,0,0,0.18)",
                    border: "1px solid rgba(17,17,17,0.08)",
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      aspectRatio: "9 / 16",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      component="video"
                      src={membresiaVideo}
                      muted
                      playsInline
                      preload="metadata"
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />

                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        background:
                          "linear-gradient(180deg, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.14) 45%, rgba(0,0,0,0.54) 100%)",
                      }}
                    />

                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Box
                        sx={{
                          width: 74,
                          height: 74,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "rgba(255,255,255,0.18)",
                          backdropFilter: "blur(12px)",
                          border: "1px solid rgba(255,255,255,0.20)",
                        }}
                      >
                        <PlayArrowRoundedIcon sx={{ fontSize: 42, color: "#ffffff", ml: "4px" }} />
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        position: "absolute",
                        left: 14,
                        right: 14,
                        bottom: 14,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 18,
                          lineHeight: 1.05,
                          letterSpacing: -0.4,
                          fontWeight: 800,
                          color: "#ffffff",
                          textShadow: "0 8px 24px rgba(0,0,0,0.50)",
                        }}
                      >
                        Consejos para entrenar mejor
                      </Typography>
                    </Box>
                  </Box>
                </MotionBox>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      <Dialog
        open={videoOpen}
        onClose={() => setVideoOpen(false)}
        fullScreen={isMobile}
        maxWidth={false}
        PaperProps={{
          sx: {
            bgcolor: "#0b0b0d",
            borderRadius: { xs: 0, sm: 4 },
            overflow: "hidden",
            width: { xs: "100vw", sm: "min(92vw, 430px)" },
            height: { xs: "100dvh", sm: "min(92vh, 780px)" },
          },
        }}
      >
        <DialogContent sx={{ p: 0, position: "relative" }}>
          <IconButton
            onClick={() => setVideoOpen(false)}
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              zIndex: 3,
              bgcolor: "rgba(0,0,0,0.5)",
              color: "#fff",
            }}
          >
            <CloseRoundedIcon />
          </IconButton>

          <Box
            component="video"
            src={membresiaVideo}
            controls
            autoPlay
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}